import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import { getUser, listUserAudit, listUserNotes, listUserRuns } from '../api/users';
import { RevealModal, type MaskableFieldKey } from '../components/RevealModal';
import { formatDate, formatBytes } from '../lib/format';

const REVEAL_TTL_MS = 15 * 60 * 1000;

interface RevealState {
  fields: MaskableFieldKey[];
  reason: string;
  expiresAt: number;
}

interface OverviewTabProps {
  u: Record<string, unknown>;
}

export function OverviewTab({ u }: OverviewTabProps): React.ReactElement {
  return (
    <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
      <div><dt className="text-text-muted">User ID</dt><dd className="font-mono text-xs mt-0.5">{String(u.userId ?? '—')}</dd></div>
      <div><dt className="text-text-muted">Plan</dt><dd className="mt-0.5">{String(u.planTier ?? '—')}</dd></div>
      <div>
        <dt className="text-text-muted">Created</dt>
        <dd className="mt-0.5">{typeof u.createdAt === 'string' ? formatDate(u.createdAt) : '—'}</dd>
      </div>
      <div><dt className="text-text-muted">Suspended</dt><dd className="mt-0.5">{u.suspended ? 'Yes' : 'No'}</dd></div>
    </dl>
  );
}

interface ConfigTabProps {
  resume: { resumeKey: string; sizeBytes: number; uploadedAt: string } | null | undefined;
  preferences: unknown;
  answerBank: Array<{ section?: string; question: string; answer: string }>;
}

const SECTION_COLORS: Record<string, string> = {
  personal: 'bg-blue-100 text-blue-800',
  employment: 'bg-emerald-100 text-emerald-800',
  experience: 'bg-purple-100 text-purple-800',
  general: 'bg-slate-100 text-slate-800',
  custom: 'bg-amber-100 text-amber-800',
};

export function ConfigTab({ resume, preferences, answerBank }: ConfigTabProps): React.ReactElement {
  return (
    <div className="space-y-4">
      <section>
        <h3 className="text-sm font-semibold text-text-secondary mb-2">Resume</h3>
        {resume ? (
          <dl className="text-sm space-y-1">
            <div className="flex gap-2"><dt className="text-text-muted w-24">Key</dt><dd className="font-mono text-xs">{resume.resumeKey}</dd></div>
            <div className="flex gap-2"><dt className="text-text-muted w-24">Size</dt><dd>{formatBytes(resume.sizeBytes)}</dd></div>
            <div className="flex gap-2"><dt className="text-text-muted w-24">Uploaded</dt><dd>{formatDate(resume.uploadedAt)}</dd></div>
          </dl>
        ) : (
          <p className="text-sm text-text-muted">No resume uploaded.</p>
        )}
      </section>
      <section>
        <h3 className="text-sm font-semibold text-text-secondary mb-2">Preferences</h3>
        {preferences ? (
          <pre className="text-xs bg-surface-muted p-3 rounded overflow-auto">{JSON.stringify(preferences, null, 2)}</pre>
        ) : (
          <p className="text-sm text-text-muted">No preferences set.</p>
        )}
      </section>
      <section>
        <h3 className="text-sm font-semibold text-text-secondary mb-2">Answer Bank</h3>
        {answerBank.length > 0 ? (
          <ul className="space-y-2 text-sm">
            {answerBank.map((e, i) => (
              <li key={i} className="bg-surface-muted rounded p-2">
                <div className="flex items-center gap-2">
                  {e.section && (
                    <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${SECTION_COLORS[e.section] ?? 'bg-slate-100 text-slate-800'}`}>
                      {e.section}
                    </span>
                  )}
                  <p className="font-medium text-text-secondary">{e.question}</p>
                </div>
                <p className="text-text-primary mt-0.5">{e.answer}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-text-muted">No answer bank entries.</p>
        )}
      </section>
    </div>
  );
}

interface RunsTabProps { isLoading: boolean; items: Array<Record<string, unknown>> }

export function RunsTab({ isLoading, items }: RunsTabProps): React.ReactElement {
  return (
    <>
      {isLoading && <p className="text-sm text-text-muted">Loading…</p>}
      <ul className="space-y-1 text-sm">
        {items.map((r) => {
          const runId = String(r.runId ?? '');
          const key = runId || String(r.SK ?? '');
          const row = (
            <div className="flex gap-4 text-text-secondary">
              <span className="font-mono text-xs">{runId.slice(0, 8)}</span>
              <span>{String(r.status ?? '—')}</span>
              <span className="text-text-muted">{typeof r.startedAt === 'string' ? formatDate(r.startedAt) : '—'}</span>
            </div>
          );
          return (
            <li key={key}>
              {runId ? (
                <Link to={`/runs/${runId}`} className="block -mx-2 px-2 py-1 rounded hover:bg-surface-subtle">
                  {row}
                </Link>
              ) : (
                row
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

interface AuditTabProps { isLoading: boolean; items: Array<Record<string, unknown>> }

export function AuditTab({ isLoading, items }: AuditTabProps): React.ReactElement {
  return (
    <>
      {isLoading && <p className="text-sm text-text-muted">Loading…</p>}
      <ul className="space-y-1 text-sm">
        {items.map((a) => (
          <li key={String(a.actionId)} className="flex gap-4 text-text-secondary">
            <span className="text-text-muted">{typeof a.createdAt === 'string' ? formatDate(a.createdAt) : '—'}</span>
            <span>{String(a.action ?? '—')}</span>
            {typeof a.reason === 'string' && <span className="text-text-muted italic">{a.reason}</span>}
          </li>
        ))}
      </ul>
    </>
  );
}

interface NotesTabProps { isLoading: boolean; items: Array<Record<string, unknown>> }

export function NotesTab({ isLoading, items }: NotesTabProps): React.ReactElement {
  return (
    <>
      {isLoading && <p className="text-sm text-text-muted">Loading…</p>}
      <ul className="space-y-2 text-sm">
        {items.map((n) => (
          <li key={String(n.noteId)} className="bg-surface-muted rounded p-3">
            <p className="text-text-primary">{String(n.content ?? '')}</p>
            <p className="text-xs text-text-muted mt-1">{String(n.actorEmail ?? '')} — {typeof n.createdAt === 'string' ? formatDate(n.createdAt) : '—'}</p>
          </li>
        ))}
      </ul>
    </>
  );
}

export function buildRevealOpts(revealState: RevealState | null): Record<string, string> {
  if (!revealState) return {};
  if (Date.now() >= revealState.expiresAt) return {};
  return { reveal: revealState.fields.join(','), reason: revealState.reason };
}

export function useRevealExpiry(revealState: RevealState | null, setRevealState: (s: RevealState | null) => void): void {
  useEffect(() => {
    if (!revealState) return;
    const remaining = revealState.expiresAt - Date.now();
    if (remaining <= 0) { setRevealState(null); return; }
    const timer = setTimeout(() => setRevealState(null), remaining);
    return () => clearTimeout(timer);
  }, [revealState, setRevealState]);
}

function useUserData(id: string | undefined, revealOpts: Record<string, string>) {
  const userQuery = useQuery({ queryKey: ['user', id, revealOpts], queryFn: () => getUser(id!, revealOpts), enabled: !!id, staleTime: 30_000 });
  const runsQuery = useQuery({ queryKey: ['user-runs', id], queryFn: () => listUserRuns(id!), enabled: !!id, staleTime: 30_000 });
  const auditQuery = useQuery({ queryKey: ['user-audit', id], queryFn: () => listUserAudit(id!), enabled: !!id, staleTime: 30_000 });
  const notesQuery = useQuery({ queryKey: ['user-notes', id], queryFn: () => listUserNotes(id!), enabled: !!id, staleTime: 30_000 });
  return { userQuery, runsQuery, auditQuery, notesQuery };
}

export function maskField(val: unknown, mask: string): string {
  return typeof val === 'string' ? val : mask;
}

export function UserDetail(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealState, setRevealState] = useState<RevealState | null>(null);

  useRevealExpiry(revealState, setRevealState);

  const revealOpts = buildRevealOpts(revealState);
  const { userQuery, runsQuery, auditQuery, notesQuery } = useUserData(id, revealOpts);

  function handleReveal(fields: MaskableFieldKey[], reason: string): void {
    setRevealState({ fields, reason, expiresAt: Date.now() + REVEAL_TTL_MS });
    void queryClient.invalidateQueries({ queryKey: ['user', id] });
  }

  if (userQuery.isLoading) return <p className="text-sm text-text-muted">Loading…</p>;
  if (userQuery.isError || !userQuery.data) return <p className="text-sm text-red-600">Failed to load user.</p>;

  const { user, preferences, answerBank, resume } = userQuery.data;
  // The reveal path may inject decrypted PII into the user blob that isn't
  // part of the masked UserItem schema (e.g. resolved phone number). Cast
  // through unknown so maskField() can read those reveal-only keys without
  // forcing them into the canonical type.
  const u = user as unknown as Record<string, unknown>;
  const isRevealed = revealState !== null && Date.now() < revealState.expiresAt;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">{maskField(u.name, '••••••')}</h1>
          <p className="text-sm text-text-secondary">{maskField(u.email, '••••••••@•••.•••')}</p>
        </div>
        <button onClick={() => setRevealOpen(true)} className="px-3 py-1.5 text-sm border border-brand-600 text-brand-600 rounded-md hover:bg-brand-50">
          Reveal PII
        </button>
      </div>
      {isRevealed && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          PII revealed for: {revealState!.fields.join(', ')} — expires in ~15 min
        </p>
      )}
      <RevealModal open={revealOpen} onOpenChange={setRevealOpen} onSubmit={handleReveal} />
      <Tabs.Root defaultValue="overview" className="bg-white border border-border rounded-lg">
        <Tabs.List className="flex border-b border-border px-4">
          {['overview', 'config', 'runs', 'audit', 'notes'].map((tab) => (
            <Tabs.Trigger key={tab} value={tab} className="px-4 py-3 text-sm font-medium capitalize text-text-secondary data-[state=active]:text-brand-700 data-[state=active]:border-b-2 data-[state=active]:border-brand-600 -mb-px">
              {tab}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        <Tabs.Content value="overview" className="p-4"><OverviewTab u={u} /></Tabs.Content>
        <Tabs.Content value="config" className="p-4"><ConfigTab resume={resume} preferences={preferences} answerBank={answerBank} /></Tabs.Content>
        <Tabs.Content value="runs" className="p-4"><RunsTab isLoading={runsQuery.isLoading} items={(runsQuery.data?.items ?? []) as Array<Record<string, unknown>>} /></Tabs.Content>
        <Tabs.Content value="audit" className="p-4"><AuditTab isLoading={auditQuery.isLoading} items={(auditQuery.data?.items ?? []) as Array<Record<string, unknown>>} /></Tabs.Content>
        <Tabs.Content value="notes" className="p-4"><NotesTab isLoading={notesQuery.isLoading} items={(notesQuery.data?.items ?? []) as Array<Record<string, unknown>>} /></Tabs.Content>
      </Tabs.Root>
    </div>
  );
}
